import { useState } from 'react';
import type { CardKind, Destination, EnergyLevel } from '@konvert/shared';
import { formatMinutesRu, zonedTimeToInstant } from '@konvert/domain';
import { t } from '../lib/i18n.js';

const TIME_PRESETS = [15, 30, 60, 120, 240];
const DURATION_PRESETS = [30, 60, 90, 120];
const KINDS: CardKind[] = ['task', 'meeting', 'reminder', 'note', 'project'];

export interface NewCardPayload {
  kind: CardKind;
  title: string;
  body?: string | null;
  fields: Record<string, unknown>;
  destination?: Destination;
}

const inputClass =
  'w-full min-w-0 rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-2 py-1.5 text-sm text-ink outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-ink-soft">{label}</span>
      {children}
    </label>
  );
}

/**
 * Creating any of the five card kinds (SPEC 4.3). Each kind asks only for what
 * it needs; meetings and reminders get their envelope from their own date, so
 * they carry no destination (SPEC 4.2).
 */
export function NewCardForm({
  date,
  timezone,
  onCreate,
}: {
  date: string;
  timezone: string;
  onCreate: (payload: NewCardPayload) => void;
}): JSX.Element {
  const [kind, setKind] = useState<CardKind>('task');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const [minutes, setMinutes] = useState<number>(30);
  const [energy, setEnergy] = useState<EnergyLevel>('green');
  const [deadline, setDeadline] = useState('');

  const [when, setWhen] = useState(date);
  const [startTime, setStartTime] = useState('10:00');
  const [duration, setDuration] = useState(60);
  const [place, setPlace] = useState('');
  const [remindTime, setRemindTime] = useState('');

  const reset = (): void => {
    setTitle('');
    setBody('');
    setPlace('');
  };

  function buildPayload(): NewCardPayload {
    const trimmed = title.trim();
    switch (kind) {
      case 'meeting': {
        // The wall clock the user picked, read in their own timezone.
        const startAt = zonedTimeToInstant(when, startTime, timezone);
        return {
          kind,
          title: trimmed,
          fields: {
            start_at: startAt.toISOString(),
            end_at: new Date(+startAt + duration * 60_000).toISOString(),
            buffer_before_min: 0,
            buffer_after_min: 0,
            location: place.trim() || null,
          },
        };
      }
      case 'reminder':
        return {
          kind,
          title: trimmed,
          fields: { remind_date: when, remind_time: remindTime || null },
        };
      case 'note': {
        const [firstLine, ...rest] = body.trim().split('\n');
        const noteTitle = trimmed || (firstLine ?? '').slice(0, 300);
        const noteBody = trimmed ? body.trim() : rest.join('\n').trim();
        return {
          kind,
          title: noteTitle,
          body: noteBody || null,
          fields: {},
          destination: { kind: 'day', date: when },
        };
      }
      case 'project':
        return { kind, title: trimmed, fields: {}, destination: { kind: 'projects' } };
      case 'task':
      default:
        return {
          kind: 'task',
          title: trimmed,
          fields: { est_minutes: minutes, energy, deadline_date: deadline || null },
          destination: { kind: 'day', date: when },
        };
    }
  }

  // What counts as "there is something to create": a note may carry only text.
  const canSubmit = title.trim().length > 0 || (kind === 'note' && body.trim().length > 0);

  const submit = (): void => {
    if (!canSubmit) return;
    onCreate(buildPayload());
    reset();
  };

  /** Capture into the backlog needs no fields at all (SPEC 4.3). */
  const capture = (): void => {
    if (!canSubmit) return;
    onCreate({
      // The backlog holds no dated cards, so those are captured as tasks.
      kind: kind === 'meeting' || kind === 'reminder' ? 'task' : kind,
      title: title.trim() || body.trim().split('\n')[0]!.slice(0, 300),
      body: kind === 'note' && title.trim() ? body.trim() || null : null,
      fields: {},
      destination: { kind: 'inbox' },
    });
    reset();
  };

  return (
    <div className="paper-card space-y-3 p-3">
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map((option) => (
          <button
            key={option}
            type="button"
            data-testid={`kind-${option}`}
            onClick={() => setKind(option)}
            className={`rounded-full border px-2.5 py-1 text-xs ${
              kind === option ? 'border-[var(--ink)] text-ink' : 'border-[var(--line)] text-ink-soft'
            }`}
          >
            {t(`kind.${option}` as 'kind.task')}
          </button>
        ))}
      </div>

      <input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && submit()}
        placeholder={
          kind === 'note' ? t('new.note_title') : t(`new.placeholder.${kind}` as 'new.placeholder.task')
        }
        className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-soft"
      />

      {kind === 'task' ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {TIME_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setMinutes(preset)}
                className={`rounded-full border px-2.5 py-1 text-xs ${
                  minutes === preset
                    ? 'border-[var(--ink)] text-ink'
                    : 'border-[var(--line)] text-ink-soft'
                }`}
              >
                {formatMinutesRu(preset)}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5">
              {(['green', 'yellow', 'red'] as EnergyLevel[]).map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setEnergy(level)}
                  aria-label={t(`energy.${level}` as 'energy.green')}
                  className={`h-6 w-6 rounded-[4px] border-2 ${
                    energy === level ? 'border-[var(--ink)]' : 'border-transparent'
                  } ${level === 'green' ? 'bg-green' : level === 'yellow' ? 'bg-amber' : 'bg-brick'}`}
                />
              ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('new.date')}>
              <input
                type="date"
                value={when}
                min={date}
                onChange={(event) => setWhen(event.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label={t('new.deadline')}>
              <input
                type="date"
                value={deadline}
                onChange={(event) => setDeadline(event.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </>
      ) : null}

      {kind === 'meeting' ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('new.date')}>
              <input
                type="date"
                value={when}
                min={date}
                onChange={(event) => setWhen(event.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label={t('new.start')}>
              <input
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DURATION_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setDuration(preset)}
                className={`rounded-full border px-2.5 py-1 text-xs ${
                  duration === preset
                    ? 'border-[var(--ink)] text-ink'
                    : 'border-[var(--line)] text-ink-soft'
                }`}
              >
                {formatMinutesRu(preset)}
              </button>
            ))}
          </div>
          <input
            value={place}
            onChange={(event) => setPlace(event.target.value)}
            placeholder={t('new.place')}
            className={`${inputClass} w-full`}
          />
        </div>
      ) : null}

      {kind === 'reminder' ? (
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('new.date')}>
            <input
              type="date"
              value={when}
              min={date}
              onChange={(event) => setWhen(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label={t('new.remind_time')}>
            <input
              type="time"
              value={remindTime}
              onChange={(event) => setRemindTime(event.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      ) : null}

      {kind === 'note' ? (
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={t('new.note_body')}
          rows={3}
          className={`${inputClass} w-full resize-none`}
        />
      ) : null}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={capture}
          disabled={!canSubmit}
          className="py-1 text-xs text-ink-soft underline disabled:opacity-40"
        >
          {t('new.to_inbox')}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="rounded-[var(--radius-card)] bg-[var(--ink)] px-4 py-2 text-sm text-[var(--paper)] disabled:opacity-40"
        >
          {t('new.create')}
        </button>
      </div>
    </div>
  );
}
